import React from 'react';
import { Database, Download, ExternalLink, Quote } from 'lucide-react';
import summary from '../data/collectSeaSummary.json';

const FILES = [
  { path: '/dataset/collectsea/data/incidents.ndjson', label: 'incidents.ndjson', desc: 'All 108 records, one JSON object per line' },
  { path: '/dataset/collectsea/data/capabilities.json', label: 'capabilities.json', desc: 'The 40-term capability vocabulary' },
  { path: '/dataset/collectsea/data/failure-mechanisms.json', label: 'failure-mechanisms.json', desc: 'The 10 failure mechanisms' },
  { path: '/dataset/collectsea/manifest.json', label: 'manifest.json', desc: 'Generated counts, so figures cannot drift from the data' },
  { path: '/dataset/collectsea/METHODOLOGY.md', label: 'METHODOLOGY.md', desc: 'How records were selected, coded and limited' },
  { path: '/dataset/collectsea/CITATION.cff', label: 'CITATION.cff', desc: 'How to cite it' }
];

export const Dataset: React.FC = () => {
  const years = Object.entries(summary.by_year);
  const maxYear = Math.max(...years.map(([, n]) => n as number));

  return (
    <div className="mx-auto max-w-4xl space-y-10 py-10">
      <header className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
            <Database className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white">
              A dataset of documented failures
            </h1>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
              Published as CollectSEA · snapshot {summary.snapshot_version} · CC BY 4.0
            </p>
          </div>
        </div>
        <p className="max-w-3xl text-base leading-relaxed text-slate-700 dark:text-slate-200">
          {summary.incidents} documented system failures, each with a link to the regulatory filing or
          engineering postmortem that reported it. An agent given control of a production system can fail the
          same ways that system already has.
        </p>
      </header>

      <section className="glass-panel rounded-3xl p-6">
        <p className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          What it is, precisely
        </p>
        <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            { label: 'Incidents', value: summary.incidents },
            { label: 'With a primary source', value: summary.incidents_with_source_url },
            { label: 'Describing AI systems', value: summary.ai_systems },
            { label: 'Describing agentic systems', value: summary.agentic_systems }
          ].map((item) => (
            <div key={item.label}>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{item.label}</dt>
              <dd className="mt-1 text-2xl font-black tabular-nums text-slate-900 dark:text-white">{item.value}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
          <strong className="font-bold">This is not an AI incident database, and calling it one would be
          misleading.</strong> Of {summary.incidents} records, {summary.ai_systems} describe AI systems and{' '}
          {summary.agentic_systems} describe agentic ones. The rest are conventional cloud, database, payment and
          network infrastructure, included deliberately as analogues. Analogy is not equivalence.
        </p>
      </section>

      <section className="glass-panel rounded-3xl p-6">
        <p className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">By year</p>
        <div className="mt-4 space-y-2">
          {years.map(([year, count]) => (
            <div key={year} className="flex items-center gap-3">
              <span className="w-12 font-mono text-xs tabular-nums text-slate-500 dark:text-slate-400">{year}</span>
              <div className="h-5 flex-1 overflow-hidden rounded bg-slate-100 dark:bg-slate-800">
                <div className="h-full rounded bg-brand-500/70" style={{ width: `${((count as number) / maxYear) * 100}%` }} />
              </div>
              <span className="w-8 text-right font-mono text-xs tabular-nums text-slate-600 dark:text-slate-300">{count as number}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white">Take it</h2>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Served directly. No account, no form, no clone required.
        </p>
        <div className="glass-panel divide-y divide-slate-900/5 rounded-2xl dark:divide-white/10">
          {FILES.map((file) => (
            <a key={file.path} href={file.path} className="flex items-center gap-3 p-4 transition hover:bg-slate-50/60 dark:hover:bg-slate-800/40">
              <Download className="h-4 w-4 flex-none text-brand-600 dark:text-brand-300" />
              <div className="min-w-0 flex-1">
                <p className="font-mono text-xs font-semibold text-slate-900 dark:text-white">{file.label}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{file.desc}</p>
              </div>
            </a>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white">A record, in full</h2>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Every record names its source, so you can check it without taking our word for it.
        </p>
        <div className="glass-panel divide-y divide-slate-900/5 rounded-2xl dark:divide-white/10">
          {summary.sample.map((item) => (
            <div key={item.id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">{item.title}</p>
                  <p className="mt-0.5 font-mono text-[11px] text-slate-500 dark:text-slate-400">
                    {item.id} · {item.date} · {item.publisher}
                  </p>
                </div>
                <a
                  href={item.source_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex flex-none items-center gap-1 text-xs font-bold text-brand-600 hover:underline dark:text-brand-300"
                >
                  Source <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="glass-panel rounded-2xl p-6">
        <div className="flex items-center gap-2">
          <Quote className="h-4 w-4 text-slate-500 dark:text-slate-400" />
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Limitations</h2>
        </div>
        <ul className="mt-3 space-y-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          <li>— Not a probability sample. Records were selected for having a citable primary source, which favours incidents somebody was obliged to disclose.</li>
          <li>— Coded by a single rater. There is no inter-rater agreement statistic because there is no second rater.</li>
          <li>— Matching weights were set by judgement, not fitted to data.</li>
          <li>— No DOI yet: minting one needs a public repository, which is a decision the author has not yet taken.</li>
        </ul>
      </section>
    </div>
  );
};
